import { useState, useEffect } from 'react'
import { AccountCredentialForm } from '@/components/integrations/AccountCredentialForm'
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { integrationsClient } from '@/lib/api/integrations/integrations.client'
import { toast } from 'sonner'
import { INTEGRATIONS, isValidEntrataConfigId } from '@super/shared-types'
import { useAppAuth } from '@/contexts/app-auth.context'
import { useAuth } from '@/hooks/use-auth'
import * as z from 'zod'
import type { EntrataMetadata } from '@/lib/api/integrations/entrata.client'

const parseEntrataUrl = (url: string): string | null => {
  try {
    const urlObj = new URL(url)
    const match = urlObj.hostname.match(/^([^.]+)\.entrata\.com$/)
    // Same rule as the API: the subdomain is only letters, digits and hyphens
    return match && isValidEntrataConfigId(match[1]) ? match[1] : null
  } catch {
    return null
  }
}

const entrataAdditionalSchema = z.object({
  entrataUrl: z
    .string()
    .min(1, 'Entrata URL is required')
    .refine(
      url => {
        try {
          new URL(url)
          return true
        } catch {
          return false
        }
      },
      { message: 'Please enter a valid URL' },
    )
    .refine(url => parseEntrataUrl(url) !== null, {
      message: 'URL must be in format: https://yourcompany.entrata.com',
    }),
  defaultProperty: z.string().optional(),
})

export interface EntrataConnectFormProps {
  isEditMode?: boolean
  onConnected: () => void
}

export function EntrataConnectForm({
  isEditMode = false,
  onConnected,
}: EntrataConnectFormProps) {
  const { currentCompany } = useAppAuth()
  const { isSuperAdmin } = useAuth()
  const [existingCredential, setExistingCredential] = useState<{
    accountIdentifier: string
  } | null>(null)
  const [entrataUrl, setEntrataUrl] = useState<string>('')
  const [defaultProperty, setDefaultProperty] = useState<string>('')
  const [isLoading, setIsLoading] = useState(isEditMode)

  useEffect(() => {
    if (!isEditMode || !currentCompany?.id) {
      setIsLoading(false)
      return
    }

    const fetchStatus = async () => {
      try {
        setIsLoading(true)
        const integrations = isSuperAdmin
          ? await integrationsClient.getAllAdminIntegrationsStatus(
              currentCompany.id,
            )
          : await integrationsClient.getAllIntegrationsStatus()
        const entrata = integrations.find(
          i =>
            i.type === INTEGRATIONS.ENTRATA.TYPE ||
            i.name === INTEGRATIONS.ENTRATA.NAME,
        )
        if (entrata?.connected && entrata.accountId) {
          setExistingCredential({ accountIdentifier: entrata.accountId })
          if (entrata.metadata?.configId) {
            setEntrataUrl(`https://${entrata.metadata.configId}.entrata.com`)
          }
          if (entrata.metadata?.defaultProperty) {
            setDefaultProperty(entrata.metadata.defaultProperty as string)
          }
        }
      } catch (error) {
        console.error('Error fetching integration status:', error)
        toast.error('Failed to load existing credentials')
      } finally {
        setIsLoading(false)
      }
    }
    fetchStatus()
  }, [isEditMode, currentCompany?.id, isSuperAdmin])

  const handleSubmit = async (data: {
    accountIdentifier: string
    password: string
    entrataUrl?: string
    defaultProperty?: string
  }) => {
    if (!currentCompany?.id) {
      toast.error('No company selected')
      return
    }

    const configId = data.entrataUrl ? parseEntrataUrl(data.entrataUrl) : null

    if (!configId) {
      toast.error('Invalid Entrata URL format')
      return
    }

    const metadata: EntrataMetadata = {
      configId,
      ...(data.defaultProperty && { defaultProperty: data.defaultProperty }),
    }

    try {
      if (isEditMode) {
        if (isSuperAdmin) {
          await integrationsClient.entrata.updateAdminCredentials(
            currentCompany.id,
            data.accountIdentifier,
            data.password,
            metadata,
          )
        } else {
          await integrationsClient.entrata.updateCredentials(
            data.accountIdentifier,
            data.password,
            metadata,
          )
        }
        toast.success('Entrata credentials updated successfully!')
      } else {
        if (isSuperAdmin) {
          await integrationsClient.entrata.createAdminCredentials(
            currentCompany.id,
            data.accountIdentifier,
            data.password,
            metadata,
          )
        } else {
          await integrationsClient.entrata.createCredentials(
            data.accountIdentifier,
            data.password,
            metadata,
          )
        }
        toast.success('Entrata integration connected successfully!')
      }
      onConnected()
    } catch (error) {
      console.error(
        `Error ${isEditMode ? 'updating' : 'connecting'} Entrata:`,
        error,
      )
      toast.error(
        `Failed to ${isEditMode ? 'update' : 'connect'} Entrata integration`,
      )
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-20 bg-secondary/50 animate-pulse rounded" />
        <div className="h-20 bg-secondary/50 animate-pulse rounded" />
      </div>
    )
  }

  return (
    <AccountCredentialForm
      onSubmit={handleSubmit}
      accountIdentifierLabel="Username"
      accountIdentifierPlaceholder="username@yourcompany"
      requireEmail={false}
      passwordLabel="Password"
      passwordPlaceholder="Enter your password"
      submitButtonText={isEditMode ? 'Update' : 'Connect'}
      initialAccountIdentifier={existingCredential?.accountIdentifier}
      additionalSchema={entrataAdditionalSchema}
      initialValues={{ entrataUrl, defaultProperty }}
      confirmationText={
        isEditMode
          ? 'Update your credentials for Entrata'
          : 'Connect your credentials for Entrata'
      }
    >
      {({ form, isSubmitting }) => (
        <>
          <FormField
            control={form.control}
            name="entrataUrl"
            render={({ field }) => (
              <FormItem className="max-w-md">
                <FormLabel>Entrata URL</FormLabel>
                <FormControl>
                  <Input
                    placeholder="https://yourcompany.entrata.com"
                    disabled={isSubmitting}
                    value={field.value as string}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    name={field.name}
                  />
                </FormControl>
                <FormDescription>
                  Your Entrata portal URL (e.g.,
                  https://yourcompany.entrata.com)
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="defaultProperty"
            render={({ field }) => (
              <FormItem className="max-w-md">
                <FormLabel>Default property (optional)</FormLabel>
                <FormControl>
                  <Input
                    placeholder="121 Main St, Anytown, CA"
                    disabled={isSubmitting}
                    value={field.value as string}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    name={field.name}
                  />
                </FormControl>
                <FormDescription>
                  The Entrata property to use when the call does not match a
                  property
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </>
      )}
    </AccountCredentialForm>
  )
}
